<template>
  <div class="envelope-template-creation">
    <v-stepper
      v-model="currentStep"
      class="envelope-template-creation__stepper"
      color="success"
    >
      <v-stepper-header class="envelope-template-creation__stepper-header">
        <v-stepper-item :value="TemplateCreationSteps.DOCUMENTS_SELECTION">
          <span class="text-text-secondary">{{ $t('project.views.EnvelopeTemplateAdd.documentsSelectionStepTitle') }}</span>
        </v-stepper-item>
        <v-divider  />
        <v-stepper-item :value="TemplateCreationSteps.TEMPLATE_SETTINGS">
          <span class="text-text-secondary">{{ $t('project.views.EnvelopeTemplateAdd.templateSettingsStepTitle') }}</span>
        </v-stepper-item>
        <v-divider />
        <v-stepper-item :value="TemplateCreationSteps.SIGNATURE_TAGS">
          <span class="text-text-secondary">{{ $t('project.views.EnvelopeTemplateAdd.signatureTagsStepTitle') }}</span>
        </v-stepper-item>
      </v-stepper-header>

      <v-stepper-window>
        <v-stepper-window-item :value="TemplateCreationSteps.DOCUMENTS_SELECTION">
          <EnvelopeTemplateAddDocumentsSelection v-if="currentStep === TemplateCreationSteps.DOCUMENTS_SELECTION" />
        </v-stepper-window-item>

        <v-stepper-window-item :value="TemplateCreationSteps.TEMPLATE_SETTINGS">
          <template v-if="isEnvelopeTemplatePending">
            <div class="text-center">
              <AppLoader />
            </div>
          </template>
          <template v-else>
            <EnvelopeTemplateAddSettings v-if="currentStep === TemplateCreationSteps.TEMPLATE_SETTINGS" :formMode="formMode" />
          </template>
        </v-stepper-window-item>

        <v-stepper-window-item :value="TemplateCreationSteps.SIGNATURE_TAGS">
          <EnvelopeTemplateAddSignatureTags v-if="currentStep === TemplateCreationSteps.SIGNATURE_TAGS"
                                            :envelope-template-to-prepare="envelopeTemplatesToPrepare[envelopeTemplatesToPrepareIndex]"
          />
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>

    <div class="envelope-template-creation__actions">
      <EnvelopeTemplateAddFormActions
        :current-step="currentStep"
        :form-mode="formMode"
        :envelope-templates-to-prepare="envelopeTemplatesToPrepare"
        :envelope-templates-to-prepare-index="envelopeTemplatesToPrepareIndex"
        :is-form-valid="!v$.$invalid"
        @cancel="onCancel"
        @exit="returnToSigningChecklist"
        @next="currentStep++"
        @next-document="envelopeTemplatesToPrepareIndex++"
        @previous="onPreviousStep"
        @previous-document="envelopeTemplatesToPrepareIndex--"
        @save-and-exit="onSaveAndExit"
        @save-and-next="onSaveAndNext"
      />
    </div>

    <SigningChecklistAddBeforeLeaveDialog
      v-if="showBeforeLeaveDialog && leaveLocation"
      @confirm="onSaveAndExit"
      @close="$router.push(leaveLocation)"
    />
    <SigningChecklistPrepareCancelDialog
      v-if="showCancelDialog"
      @confirm="returnToSigningChecklist(true)"
      @close="showCancelDialog = false"
    />
  </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { cloneDeep } from 'lodash-es'
import { defineComponent, PropType } from 'vue'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { mapGetters, mapState } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import { type FormMode } from '@/common/utils/form'
import { typedMapActions, typedMapMutations } from '@/common/utils/store'
import { toast } from '@/common/utils/toast'
import { ENVELOPE_TYPES, SIGNATURE_PROVIDER } from '@/project/signing-checklist/constants'
import SigningChecklistAddBeforeLeaveDialog from '@/project/signing-checklist/dialogs/SigningChecklistAddBeforeLeaveDialog.vue'
import SigningChecklistPrepareCancelDialog from '@/project/signing-checklist/dialogs/SigningChecklistPrepareCancelDialog.vue'
import EnvelopeTemplateAddDocumentsSelection
   from '@/project/signing-checklist/envelope-templates/add/EnvelopeTemplateAddDocumentsSelection.vue'
import EnvelopeTemplateAddFormActions
  from '@/project/signing-checklist/envelope-templates/add/EnvelopeTemplateAddFormActions.vue'
import EnvelopeTemplateAddSettings
  from '@/project/signing-checklist/envelope-templates/add/EnvelopeTemplateAddSettings.vue'
import EnvelopeTemplateAddSignatureTags
  from '@/project/signing-checklist/envelope-templates/add/EnvelopeTemplateAddSignatureTags.vue'
import { LOGOUT_ROUTE_NAME, SIGNING_CHECKLIST_ROUTE_NAME } from '@/router/routerData'
import { type EnvelopeTemplatesActionType } from '@/store/modules/envelope-templates/action_types'
import { type EnvelopeTemplatesMutationType } from '@/store/modules/envelope-templates/mutation_types'

export enum TemplateCreationSteps {
  DOCUMENTS_SELECTION = 1,
  TEMPLATE_SETTINGS = 2,
  SIGNATURE_TAGS = 3,
}

export type EnvelopeTemplateToPrepare = {
  id: number
  envelopeType: ENVELOPE_TYPES
  provider: SIGNATURE_PROVIDER
  signatureType: string
}

type DataType = {
  currentStep: TemplateCreationSteps,
  envelopeTemplatesToPrepare: Array<EnvelopeTemplateToPrepare>
  envelopeTemplatesToPrepareIndex: number
  leaveLocation: object | null
  showBeforeLeaveDialog: boolean
  showCancelDialog: boolean
  isEnvelopeTemplatePending: boolean
  initialTemplatesState: Array<object> | null
}

type EnvelopeType = typeof ENVELOPE_TYPES[keyof typeof ENVELOPE_TYPES]

type EnvelopeTemplate = {
  id: number
  title: string
  envelopeType: EnvelopeType
  provider: SIGNATURE_PROVIDER
  signatureType: string
}



export default defineComponent({
  name: 'EnvelopeTemplateAdd',
  components: {
    AppLoader,
    EnvelopeTemplateAddSignatureTags,
    EnvelopeTemplateAddFormActions,
    EnvelopeTemplateAddDocumentsSelection,
    EnvelopeTemplateAddSettings,
    SigningChecklistAddBeforeLeaveDialog,
    SigningChecklistPrepareCancelDialog,
  },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    formMode: {
      type: String as PropType<FormMode>,
      default: 'creation',
    },
  },
  data (): DataType {
    return {
      currentStep: this.formMode === 'edition'
        ? TemplateCreationSteps.TEMPLATE_SETTINGS
        : TemplateCreationSteps.DOCUMENTS_SELECTION,
      envelopeTemplatesToPrepare: [],
      envelopeTemplatesToPrepareIndex: 0,
      leaveLocation: null,
      showBeforeLeaveDialog: false,
      showCancelDialog: false,
      isEnvelopeTemplatePending: false,
      initialTemplatesState: null,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    ...mapState('envelopeTemplates', ['envelopeTemplates', 'envelopeTemplateDrafts']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    TemplateCreationSteps () { return TemplateCreationSteps },
  },
  head () {
    return {
      title: this.$t('project.views.EnvelopeTemplateAdd.metaInfo.title').toString(),
    }
  },
  async created () {
    if (this.formMode === 'edition' && this.$route.query['envelopeTemplateId']) {
      try {
        this.isEnvelopeTemplatePending = true
        await this.UPDATE_DRAFT_TEMPLATE_DATA(this.$route.query['envelopeTemplateId'])
      } catch (e) {
        toast.error(this.$t('project.views.EnvelopeTemplateAdd.updateDraftError'))
      } finally {
        this.isEnvelopeTemplatePending = false
        this.initialTemplatesState = cloneDeep(this.envelopeTemplateDrafts)
      }
    }
  },
  methods: {
    ...typedMapMutations<EnvelopeTemplatesMutationType>('envelopeTemplates', ['RESET_DRAFT_ENVELOPE_TEMPLATES', 'SET_TEMPLATE_DOCUMENT_ID_AFTER_CREATION_SUCCESS']),
    ...typedMapActions<EnvelopeTemplatesActionType>('envelopeTemplates', ['PATCH_ENVELOPE_TEMPLATE', 'POST_ENVELOPE_TEMPLATE', 'UPDATE_DRAFT_TEMPLATE_DATA']),
    onCancel () {
      if (this.currentStep > 1) {
        this.showCancelDialog = true
      } else {
        this.returnToSigningChecklist()
      }
    },
    onPreviousStep () {
      if (this.currentStep === TemplateCreationSteps.SIGNATURE_TAGS) {
        this.envelopeTemplatesToPrepare = []
      }

      this.currentStep -= 1
    },
    async onSaveAndExit () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        await this.saveEnvelopeTemplates(null, true)

        if (!this.leaveLocation) {
          this.returnToSigningChecklist()
        }
      }
    },
    async onSaveAndNext () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        await this.saveEnvelopeTemplates()

        if (this.envelopeTemplatesToPrepare.length > 0) {
          this.currentStep++
        }
      }
    },
    async saveEnvelopeTemplates (envelopeTemplates: object[] | null = null, displaySuccessMessage = false) {
      const envelopeTemplatesArray: Array<DraftEnvelopeTemplate> = envelopeTemplates || this.envelopeTemplateDrafts
      const envelopeTemplatesPayload = envelopeTemplatesArray.map(templateDraft => {
        const { document, templateId } = templateDraft

        return {
          templateId: templateId,
          envelopeType: document.objectToPost.envelopeType,
          fileId: document.objectToPost.fileId,
          sendNotif: document.objectToPost.sendNotif,
          shareType: document.objectToPost.shareType,
          signatureType: document.objectToPost.signatureType,
          signers: document.objectToPost.signers,
          title: document.objectToPost.title,
          withUsers: document.objectToPost.withUsers,
        }
      })

      const envelopeTemplatesPromises = envelopeTemplatesPayload.map(payload => {
        const saveTemplateSuccessCallback = (response: ObjectWith<{ envelopeTemplate: EnvelopeTemplate }>) => {
          this.SET_TEMPLATE_DOCUMENT_ID_AFTER_CREATION_SUCCESS({
            fileId: payload.fileId,
            templateId: response.envelopeTemplate.id,
          })

          return response
        }

        if (this.formMode === 'edition') {
          return this.PATCH_ENVELOPE_TEMPLATE(payload)
            .then(saveTemplateSuccessCallback)
        } else {
          return this.POST_ENVELOPE_TEMPLATE(payload)
            .then(saveTemplateSuccessCallback)
        }
      })

      const promisesResult = await Promise.allSettled(envelopeTemplatesPromises)

      promisesResult.forEach(promiseResult => {
        if (promiseResult.status === 'fulfilled') {
          if (displaySuccessMessage) {
            if (this.formMode === 'edition') {
              toast.success(this.$t(
                'project.views.EnvelopeTemplateAdd.patchEnvelopeTemplateSuccess',
                { templateTitle: promiseResult.value.envelopeTemplate.title },
              ))
            } else {
              toast.success(this.$t(
                'project.views.EnvelopeTemplateAdd.postEnvelopeTemplateSuccess',
                { templateTitle: promiseResult.value.envelopeTemplate.title },
              ))
            }
          }

          const { envelopeTemplate } = promiseResult.value
          this.envelopeTemplatesToPrepare.push({
            id: envelopeTemplate.id,
            envelopeType: envelopeTemplate.envelopeType,
            provider: envelopeTemplate.provider,
            signatureType: envelopeTemplate.signatureType,
          })
        }

        if (promiseResult.status === 'rejected') {
          toast.error(this.$t('project.views.EnvelopeTemplateAdd.postEnvelopeTemplateError'))
        }
      })
    },
    async returnToSigningChecklist (fromCancellation = false) {
      if (this.formMode === 'edition' && fromCancellation) {
        await this.saveEnvelopeTemplates(this.initialTemplatesState, false)
      }
      this.leaveLocation = { name: SIGNING_CHECKLIST_ROUTE_NAME, query: { openDocumentTemplatesDialog: true } }
      this.$router.push(this.leaveLocation)
    },
  },
  beforeUnmount () {
    this.RESET_DRAFT_ENVELOPE_TEMPLATES()
  },
  beforeRouteLeave (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    const isNavigationToLogout = to.name === LOGOUT_ROUTE_NAME
    if (isNavigationToLogout) {
      next()
    }

    if (this.currentStep === TemplateCreationSteps.TEMPLATE_SETTINGS && this.leaveLocation === null) {
      this.leaveLocation = to
      this.showBeforeLeaveDialog = true
      next(false)
    } else {
      next()
    }
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.envelope-template-creation {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__stepper.v-stepper {
    background: transparent;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 0;
  }

  &__stepper-header.v-stepper-header {
    margin: 0 auto;

    .v-stepper-item--selected > .v-stepper-item__avatar {
      background-color: rgb(var(--v-theme-primary)) !important;
    }

    .v-divider {
      width: 32px;
    }
  }

  .v-stepper-window {
    background: rgb(var(--v-theme-white));
    @include border-radius('border-radius-3');
    padding: 24px;
    margin-inline: 16px;
    margin-block: 0;
    flex: 1;
    overflow-y: auto;

    :deep(.v-window__container),
    :deep(.v-window-item) {
      height: 100%;
    }
  }

  &__actions {
    padding: 16px;
  }
}
</style>
